import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    HalfYear,
    Recurrence,
    ControlsPageFiltersForControlsHalfYearlyPageSpecificFilters as ControlsHalfYearlyFilters,
    ControlsHalfYearlyPageSpecificFilters,
    ControlsHalfYearlyColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsHalfYearlyColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    recurrenceHalfYearly: null,
    notification: null,
    scheduleHalfYearly: null,
    status: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    financialYear: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsHalfYearlyColumn[] = Object.keys(
    allColumnsGuard
) as ControlsHalfYearlyColumn[];

const encodeControlsHalfYearlyColumn: (
    col: string
) => ControlsHalfYearlyColumn = createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsHalfYearlySpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsHalfYearlyPageSpecificFilters {
    return {
        notification: encodeOrFilter<number>("notification", filters),
        financialYear: encodeOrFilter<number>("financialYear", filters),
        recurrenceHalfYearly: encodeOrFilter<Recurrence>(
            "recurrenceHalfYearly",
            filters
        ),
        scheduleHalfYearly: encodeOrFilter<HalfYear[]>(
            "scheduleHalfYearly",
            filters
        ),
    };
}
const encodeControlsHalfYearlyPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsHalfYearlyFilters = createEncodePageFiltersFn(
    encodeControlsHalfYearlySpecificPageFilters
);

function decodeControlsHalfYearlyPageSpecificFilters(
    specificFilters: ControlsHalfYearlyPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.notification) {
        output.push(
            decodeOrFilter<number>("notification", specificFilters.notification)
        );
    }
    if (specificFilters.financialYear) {
        output.push(
            decodeOrFilter<number>(
                "financialYear",
                specificFilters.financialYear
            )
        );
    }
    if (specificFilters.recurrenceHalfYearly) {
        output.push(
            decodeOrFilter<Recurrence>(
                "recurrenceHalfYearly",
                specificFilters.recurrenceHalfYearly
            )
        );
    }
    if (specificFilters.scheduleHalfYearly) {
        output.push(
            decodeOrFilter<HalfYear[]>(
                "scheduleHalfYearly",
                specificFilters.scheduleHalfYearly
            )
        );
    }
    return output;
}
const decodeControlsHalfYearlyPageFilters: (
    pageFilters: ControlsHalfYearlyFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsHalfYearlyPageSpecificFilters
);

// Page state
//
type ControlsHalfYearlyPageStateWithType =
    PageStateData<"ControlsHalfYearly"> & { type: "ControlsHalfYearly" };

function decodeControlsHalfYearlyPageState(
    pageState: ControlsHalfYearlyPageStateWithType
): PageFilterAndColumns {
    const halfYearlylPageState = unwrapPageStateAs(
        pageState,
        "ControlsHalfYearly"
    );
    const filter: SimpleAndFilter = {
        filters: decodeControlsHalfYearlyPageFilters(
            halfYearlylPageState.filters
        ),
    };
    const visibleColumnIds = new Set(halfYearlylPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsHalfYearlyPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsHalfYearlyPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsHalfYearly",
        filters: encodeControlsHalfYearlyPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(
            encodeControlsHalfYearlyColumn
        ),
        version: filterAndColumns.version,
    };
}

export { encodeControlsHalfYearlyPageState, decodeControlsHalfYearlyPageState };

export type { ControlsHalfYearlyPageStateWithType };
